import { Schema, SchemaGetter } from "effect"
import { Actor, AgentKind, Effort, ModelProvider, RecordMeta } from "../common"
import {
  AgentId,
  AreaId,
  CredentialId,
  Handle,
  IntegrationId,
  Markdown,
  Millis,
  PolicyId,
  ProfileId,
  ProjectId,
  Rank,
  Slug,
  UserId,
} from "../ids"
import { PlacementDefaults, Requires } from "../placement"
import { AccessRequest } from "../runner"
import { Hands } from "./item"

// #region area
/** Written by people; read by the area's (or agent's) runs on every turn. Revisions are events with before/after. */
export const Charter = Schema.Struct({ mission: Schema.String, body: Markdown })

/** Shared new-lead defaults. Existing charters remain their author's instructions. */
export const defaultLeadMission = (projectName: string): string =>
  `Lead ${projectName.trim() || "the project"}: turn goals into small, checkable items and keep them moving.`

export const DEFAULT_LEAD_CHARTER_BODY = [
  "- Plan top-level items (releases, bets, workstreams): usually one in Now per area and three per project; more is allowed, and `plan` warns.",
  "- Keep tracked implementation small: one pull request each. Use native turns for investigation, knowledge and other delegated work.",
  "- Done checks demonstrate the outcome: discover documented repository checks for code, and use artifacts or rubrics for other work.",
  "- Split an item when it is too big for one attempt, or when a failed attempt shows it needs a different approach.",
  "- Run work in parallel only when it is independent.",
  "- Once the person says go, put real output in front of them and the people it is for early: the core of what they asked for before breadth or polish.",
  "- Investigate routine uncertainty with the context and tools available. Make reversible choices within your delegation; use `ask_human` for a reserved decision or essential missing input.",
].join("\n")

export const defaultLeadCharter = (
  projectName: string,
): { readonly mission: string; readonly body: string } => ({
  mission: defaultLeadMission(projectName),
  body: DEFAULT_LEAD_CHARTER_BODY,
})

export const IntegrationGrant = Schema.Struct({
  integrationId: IntegrationId,
  read: Schema.Array(Schema.String), // tool names
  write: Schema.Array(Schema.String),
  /** Only the publish step may execute these, after a human approved the exact call. Never in a worker's grant. */
  outward: Schema.Array(Schema.String),
  grantedBy: UserId,
  grantedAt: Millis,
})

/**
 * Rule of Two archetype for runs in this area (Research/SEO = untrusted + external). Any two may be true. All three
 * are allowed only because every external effect is then approved by a person: agents never hold outward_action,
 * `outward` tools are granted only with externalEffects, and with all three powers on, nothing but person-approved
 * `outward` tools may write (decide rejects `write` grants).
 */
export const AreaPowers = Schema.Struct({
  untrustedInput: Schema.Boolean,
  privateData: Schema.Boolean,
  externalEffects: Schema.Boolean,
})

export const AreaInterface = Schema.Struct({ key: Slug, title: Schema.String, description: Markdown })

export const Area = Schema.Struct({
  ...RecordMeta,
  id: AreaId,
  slug: Slug,
  name: Schema.String, // "Growth"
  color: Schema.String, // lane colour token
  rank: Rank, // lane order
  charter: Charter,
  /**
   * The area's agent (decision 0035; the name is kept): it owns the area's new top-level items by default and accepts
   * requests from other areas. Items under an agent-owned parent take the parent's owner instead.
   */
  intakeAgentId: Schema.NullOr(AgentId),
  policyId: Schema.NullOr(PolicyId), // null = project policy
  grants: Schema.Array(IntegrationGrant), // Phase 2 (tool gateway); agents and workers in the area inherit them
  placement: PlacementDefaults, // e.g. Release requires xcode@26; Growth prefers trust=cloud
  powers: AreaPowers,
  /** At most this many top-level plannable items in Now in the area (default 1). */
  wip: Schema.Struct({ maxNow: Schema.Int }),
  interfaces: Schema.Struct({
    publishes: Schema.Array(AreaInterface),
    consumes: Schema.Array(Schema.Struct({ areaId: AreaId, key: Slug })),
  }),
  template: Schema.NullOr(Schema.Struct({ templateId: Schema.String, version: Schema.Int })),
  status: Schema.Literals(["active", "archived"]),
})
export type Area = typeof Area.Type
// Rules, facts, learnings and playbooks of an area are MemoryItems with scope {area}. Not fields here.
// #endregion area

// #region agent
/** Where the agent thinks. The body (its Agent DO) never moves. No silent fallback between minds. */
const CurrentMind = Schema.TaggedUnion({
  /** Default. Turns run inside the Agent DO via the provider API (streamText) with in-process graph tools. */
  platform: {
    provider: ModelProvider,
    model: Schema.String, // pinned per agent (model brittleness guardrail)
    credential: Schema.TaggedUnion({ workspace_default: {} }),
  },
  /** The local CLI on a runner, on its owner's subscription. `requires.runner` pins a machine; turns wait while it is away. */
  runner: {
    agent: AgentKind,
    requires: Requires,
    profileId: Schema.NullOr(ProfileId),
    model: Schema.NullOr(Schema.String),
    effort: Schema.NullOr(Effort),
    /** Native profile tools by default; explicitly selected MCP or read-only narrowing is retained. */
    access: Schema.optionalKey(AccessRequest),
  },
})

/**
 * Decode-only: mind shapes nothing writes any more but stored events, snapshots and commands may still hold. The
 * `hosted` placeholder (Managed Agents, never built) reads as a platform mind on Anthropic; a platform mind's named
 * `credential` (never resolved; turns always used the workspace key) reads as `workspace_default`.
 */
const RetiredMind = Schema.Union([
  Schema.TaggedStruct("hosted", { vendor: Schema.Literal("anthropic_managed"), model: Schema.String }),
  Schema.TaggedStruct("platform", {
    provider: ModelProvider,
    model: Schema.String,
    credential: Schema.TaggedStruct("credential", { credentialId: CredentialId }),
  }),
]).pipe(
  Schema.decodeTo(Schema.toType(CurrentMind), {
    decode: SchemaGetter.transform((m): typeof CurrentMind.Type => ({
      _tag: "platform",
      provider: m._tag === "hosted" ? "anthropic" : m.provider,
      model: m.model,
      credential: { _tag: "workspace_default" },
    })),
    encode: SchemaGetter.forbidden(() => "retired mind shapes are never encoded"),
  }),
)

export const Mind = Schema.Union([CurrentMind, RetiredMind])
export type Mind = typeof Mind.Type

export const AgentRole = Schema.Literals(["mayor", "lead", "role"])

/**
 * How far an agent goes in a turn that changes started (decision 0048): `suggest` Suggest in chat, `ask` Ask first,
 * `act` Act, then tell me. Turns a person started always use the full policy.
 */
export const Autonomy = Schema.Literals(["suggest", "ask", "act"])
export type Autonomy = typeof Autonomy.Type

/**
 * Optional daily budget for monitor and recurring-item wakes in the project's zone. A configured cap holds further
 * turns on a card. Null or absent means no daily cap. Only the keeper or an admin changes this budget.
 */
export const TimedCap = Schema.NullOr(Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)))
export const DEFAULT_TIMED_CAP = null

/**
 * The agent's identity and configuration. Project agents live in the Project DO roster (so policy is checked in
 * the same transaction as graph edits); the Mayor's record lives in the Workspace DO. Living state (chat, inbox,
 * notebook, monitors, session mirror) lives in the agent's own Agent DO. Presence is derived, never stored.
 */
export const Agent = Schema.Struct({
  ...RecordMeta, // ownerId = the principal of every run this agent starts; only the owner (or an admin) steers it
  id: AgentId,
  role: AgentRole,
  projectId: Schema.NullOr(ProjectId), // null for the Mayor
  /** Role agents belong to one area, or to none (project-wide, settings wave K7); the lead and Mayor to none. */
  areaId: Schema.NullOr(AreaId),
  name: Schema.String, // "Mara"
  handle: Handle, // @mara
  title: Schema.String, // "SEO strategist"
  avatar: Schema.Struct({ emoji: Schema.NullOr(Schema.String), color: Schema.String }),
  charter: Charter,
  mind: Mind,
  /** Legacy worker defaults: a last fallback after the item and the project's worker presets (settings wave §5.5). */
  hands: Hands,
  policyId: PolicyId, // agent-level override; effective = project ⊕ area ⊕ agent
  /** Minimum interval between turns caused by non-human wakes. Human messages bypass it. */
  wake: Schema.Struct({ debounceMs: Schema.Int }),
  /** Decision 0048: how far the agent goes in a turn that changes started. Absent = "act" (today's behaviour). */
  autonomy: Schema.optionalKey(Autonomy),
  status: Schema.Literals(["active", "paused", "retired"]),
  /**
   * Wave C (decision 0071): who last revised the charter (mission and instructions) and when; written by evolve from
   * AgentCharterRevised's envelope. Absent = never revised since hire (or before wave C).
   */
  charterEdit: Schema.optionalKey(Schema.Struct({ by: Actor, at: Millis })),
  /** Wave C: timed turns per day (see {@link TimedCap}); absent = {@link DEFAULT_TIMED_CAP}. */
  timedCap: Schema.optionalKey(TimedCap),
  /** Optional coordination budget: hold agent-caused wakes at this chain length. Null/absent has no count gate. */
  chainCap: Schema.optionalKey(TimedCap),
})
export type Agent = typeof Agent.Type
// #endregion agent

// #region policy
/**
 * The closed set of things a policy can talk about (decision 0037). One capability per graph command family; merged
 * item capabilities keep the v1 split through the `level` constraint.
 */
export const Capability = Schema.Literals([
  "create_goal",
  "update_goal",
  "change_kpi_target",
  "define_kpi",
  "create_area",
  "update_area",
  "hire_agent",
  "update_agent",
  // the brief (decision 0049): no preset mentions it; an agent's change is always a proposal (forced rule), `never` refuses it
  "revise_brief",
  "create_item",
  "update_item",
  "start_item", // a top-level item moving into or out of Now
  "cancel_item",
  "complete_item", // close an open item as done (CloseItem{done})
  "schedule_work", // an item's recurrence and Run now
  "coordinate_projects",
  "revise_checks",
  "reprioritize",
  "assign",
  "add_dependency",
  "remove_dependency",
  "spawn_workers",
  "propose_memory",
  "curate_memory",
  // forced to `never` for every agent (structural): no tool exists, and decide rejects anyway
  "outward_action",
  "edit_policy",
  "grant_integration",
  // decision 0084 (§1.5): the Ready gate. Moving Planning (triage/backlog) work to Ready or Now, or creating it there.
  // Checked as a second verdict only when some policy layer mentions it, so existing policies keep their behaviour.
  "ready_item",
])
export type Capability = typeof Capability.Type

/** Typed constraints, evaluated against the current state by core (deterministic and replayable). */
export const RuleConstraints = Schema.Struct({
  /** own_tree: the top-level item's owner is the agent; own_item: the item's owner is the agent (a worker: its item). */
  under: Schema.optionalKey(Schema.Literals(["existing_goal", "own_area", "own_tree", "own_item"])),
  /** The target item is (will be) top-level, or has a parent. A rule with a level never holds without an item target. */
  level: Schema.optionalKey(Schema.Literals(["top", "child"])),
  /** create_item: open plannable top-level items; start_item and others: Now items. */
  maxActive: Schema.optionalKey(Schema.Int),
  maxConcurrent: Schema.optionalKey(Schema.Int), // spawn_workers: live worker runs for this agent
  to: Schema.optionalKey(Schema.Literals(["role_agents", "own_area_agents"])), // assign: the new owner
  ifWorkDone: Schema.optionalKey(Schema.Boolean), // cancel_item: any attempt in the item's tree
  maxDepth: Schema.optionalKey(Schema.Literals([0, 1, 2, 3])), // depth below the top-level item
  /**
   * Decision 0138: revise_checks only. `true` also covers checks a person froze; without it an agent's change to them
   * is a card (frozen checks became a policy rule instead of a person-only rule).
   */
  frozen: Schema.optionalKey(Schema.Boolean),
})
export type RuleConstraints = typeof RuleConstraints.Type
export const PolicyRule = Schema.Struct({ capability: Capability, when: Schema.optionalKey(RuleConstraints) })
export type PolicyRule = typeof PolicyRule.Type

export const PolicyDocument = Schema.Struct({
  allow: Schema.Array(PolicyRule),
  propose: Schema.Array(PolicyRule),
  never: Schema.Array(Capability),
})
export type PolicyDocument = typeof PolicyDocument.Type

export const Policy = Schema.Struct({
  ...RecordMeta,
  id: PolicyId,
  subject: Schema.TaggedUnion({ project: {}, area: { areaId: AreaId }, agent: { agentId: AgentId } }),
  /** Versioned. Rollback = a new revision whose document equals an older one (`restoredFrom`). */
  revision: Schema.Int,
  document: PolicyDocument,
  restoredFrom: Schema.NullOr(Schema.Int),
})
export type Policy = typeof Policy.Type
// #endregion policy
